import React, { FC } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { ShortCodeEnums } from '@nhl/nhl-breakaway-ui';

import { FAKE_TBD_TEAM_ID } from '~/core/constants';
import { LocalizedAttribute } from '~/core/types';
import { parseValueFromLocalizedAttribute } from '~/core/utils';

import { NameContainer, StrongName, WeakName } from './styles';

type Props = {
    alignRight?: boolean;
    teamCommonName: LocalizedAttribute;
    teamId: number;
    teamPlaceNameWithPreposition: LocalizedAttribute;
    addSpace?: boolean;
};

/*
 * This is a workaround for Utah's team name from the 2024-2025 season. The common name is Utah Hockey Club
 * and the place name is Utah. We use the team ID so we can avoid showing Utah Utah Hockey Club.
 */
const UTAH_2425_TEAM_ID = 59;

const FAKE_UTAH_2425_COMMON_NAME: LocalizedAttribute = {
    default: 'Hockey Club',
    [ShortCodeEnums.Language.French]: 'Club de hockey',
};

const LocalizedTeamName: FC<Props> = ({
    alignRight,
    teamCommonName,
    teamId,
    teamPlaceNameWithPreposition,
    addSpace,
}) => {
    const intl = useIntl();
    const is2425Utah = teamId === UTAH_2425_TEAM_ID;
    const isTBD = teamId === FAKE_TBD_TEAM_ID;

    return (
        <NameContainer $isAlignedRight={alignRight}>
            <FormattedMessage
                description="Displays a team name with separate styling for the place name and the common name. For example, the 'Philadelphia Flyers' would display 'Philadelphia' in a smaller font and 'Flyers' in a larger font. Translators can change the ordering of these terms and they will have appropriate prepositions. For example, in French the two terms will be 'de Philadelphia' and 'Flyers' and should be re-ordered to display 'Flyers de Philadelphia'."
                defaultMessage="<weak>{placeName}</weak><strong>{commonName}</strong>"
                values={{
                    placeName: isTBD ? '' : parseValueFromLocalizedAttribute(teamPlaceNameWithPreposition, intl.locale),
                    commonName: parseValueFromLocalizedAttribute(
                        is2425Utah ? FAKE_UTAH_2425_COMMON_NAME : teamCommonName,
                        intl.locale
                    ),
                    weak: (text) => (addSpace ? <WeakName>{text}&nbsp;</WeakName> : <WeakName>{text}</WeakName>),
                    strong: (text) =>
                        addSpace ? <StrongName>{text}&nbsp;</StrongName> : <StrongName>{text}</StrongName>,
                }}
            />
        </NameContainer>
    );
};

export default LocalizedTeamName;
